import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { postToAdPopcorn } from '@pocapan/shared/utils/adpopcorn';

type AdPopcornEvent = 'offerwallOpened' | 'offerwallClosed' | 'rewardInfoReceived';

declare global {
  interface Window {
    AdPopcornRewardCallback?: (event: AdPopcornEvent, data: unknown) => void;
  }
}

export const useAdPopcorn = () => {
  const [rewardInfo, setRewardInfo] = useState<unknown>(null);
  const [isOfferwallOpen, setIsOfferwallOpen] = useState(false);
  const shouldFetchOnReturnRef = useRef(false);

  const queryClient = useQueryClient();

  const openOfferwall = useCallback(() => {
    shouldFetchOnReturnRef.current = true;
    postToAdPopcorn('openOfferwall');
  }, []);

  const closeOfferwall = useCallback(() => {
    postToAdPopcorn('closeOfferwall');
  }, []);

  const getRewardInfo = useCallback(() => {
    postToAdPopcorn('getRewardInfo');
  }, []);

  const setUserId = useCallback((userId: string) => {
    postToAdPopcorn('setUserId', { userId });
  }, []);

  useEffect(() => {
    // iOS 콜백 함수 등록 (iOS 네이티브에서 직접 호출)
    window.AdPopcornRewardCallback = (event: AdPopcornEvent, data: unknown) => {
      switch (event) {
        case 'offerwallOpened':
          setIsOfferwallOpen(true);
          shouldFetchOnReturnRef.current = true;
          break;
        case 'offerwallClosed':
          setIsOfferwallOpen(false);
          getRewardInfo();
          shouldFetchOnReturnRef.current = false;
          break;
        case 'rewardInfoReceived':
          setRewardInfo(typeof data === 'string' ? safeJsonParse(data) : data);
          // 리워드 수신 시 폴백 비활성화
          shouldFetchOnReturnRef.current = false;
          break;
      }
    };

    return () => {
      delete window.AdPopcornRewardCallback;
    };
  }, [getRewardInfo]);

  useEffect(() => {
    // Android CustomEvent 리스너 등록
    const handleOfferwallOpened = () => {
      setIsOfferwallOpen(true);
      shouldFetchOnReturnRef.current = true;
    };
    const handleOfferwallClosed = () => {
      setIsOfferwallOpen(false);
      getRewardInfo();
      shouldFetchOnReturnRef.current = false;
    };
    const handleRewardInfo = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const parsed = typeof detail === 'string' ? safeJsonParse(detail) : detail;
      setRewardInfo(parsed);
      shouldFetchOnReturnRef.current = false;
    };

    window.addEventListener('adpopcorn:offerwallOpened', handleOfferwallOpened as EventListener);
    window.addEventListener('adpopcorn:offerwallClosed', handleOfferwallClosed as EventListener);
    window.addEventListener('adpopcorn:rewardInfoReceived', handleRewardInfo as EventListener);

    return () => {
      window.removeEventListener(
        'adpopcorn:offerwallOpened',
        handleOfferwallOpened as EventListener
      );
      window.removeEventListener(
        'adpopcorn:offerwallClosed',
        handleOfferwallClosed as EventListener
      );
      window.removeEventListener('adpopcorn:rewardInfoReceived', handleRewardInfo as EventListener);
    };
  }, [getRewardInfo]);

  useEffect(() => {
    const handleVisibilityOrFocus = (e: Event) => {
      const isVisible =
        document.visibilityState === 'visible' || e.type === 'focus' || e.type === 'pageshow';
      if (isVisible && shouldFetchOnReturnRef.current) {
        getRewardInfo();
        queryClient.invalidateQueries(['bettieBalances']);
        shouldFetchOnReturnRef.current = false;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('pageshow', handleVisibilityOrFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('pageshow', handleVisibilityOrFocus);
    };
  }, [getRewardInfo, queryClient]);

  return {
    openOfferwall,
    closeOfferwall,
    getRewardInfo,
    setUserId,
    rewardInfo,
    isOfferwallOpen
  };
};

const safeJsonParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};
